import React from "react";
import { useTranslation } from "react-i18next";
import CheckboxFilterGroup from "@/components/Shared/FilterGroup/CheckboxFilterGroup";
import { SalaryBatchFilter } from "../../../models/Salary";
import { SalaryBatchFilterOptions } from "./useSalaryBatchFilterOptions";

interface Props {
	filter: SalaryBatchFilter;
	onChange: (filter: SalaryBatchFilter) => void;
	options: SalaryBatchFilterOptions;
	/** Locked once the batch exists, matching the date fields. */
	disabled?: boolean;
}

/**
 * Consultant scoping for a salary run: pool and subsidiary as checkbox lists,
 * matching the invoice batch form. Semantics are OR within a group and AND
 * across groups, matching what the backend applies to the consultant query.
 * Each group hides itself when the org has nothing to pick from.
 */
const SalaryBatchFilters: React.FC<Props> = ({ filter, onChange, options, disabled }) => {
	const { t } = useTranslation();

	// An emptied group is dropped rather than left as [], so an unconstrained
	// filter serialises as {}
	function setGroup(group: "pools" | "subsidiaryIds", selected: string[]) {
		const next = { ...filter };
		if (selected.length) next[group] = selected;
		else delete next[group];
		onChange(next);
	}

	return (
		<>
			<CheckboxFilterGroup
				label={t("POOL" as any)}
				options={options.pools}
				selected={filter.pools ?? []}
				onChange={(selected) => setGroup("pools", selected)}
				disabled={disabled}
				allLabel={t("batchFilter.allPools")}
			/>

			<CheckboxFilterGroup
				label={t("SUBSIDIARY")}
				options={options.subsidiaries}
				selected={filter.subsidiaryIds ?? []}
				onChange={(selected) => setGroup("subsidiaryIds", selected)}
				disabled={disabled}
				allLabel={t("batchFilter.allSubsidiaries")}
			/>
		</>
	);
};

export default SalaryBatchFilters;
